import * as THREE from 'three'
import { RenderedFaces } from '@thatopen/fragments'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { caixaDeCorteA } from './bcfElementosCena'

/**
 * "Cores distintas" nos elementos ligados a um tópico BCF: cada
 * elemento da sua cor, o resto do modelo em fantasma.
 *
 * O padrão é o do realce dos conflitos (`viewer/clash/clashHighlight.ts`),
 * pelas mesmas razões:
 *
 * - É um overlay do Highlighter e não tinta: quem tinha o modelo
 *   colorido por critério, pelo Comparar IFC ou pelo IDS não perde nada,
 *   e tudo volta como estava quando o realce sai.
 * - O estilo `select` ganha a todos no Highlighter. Por isso o realce
 *   limpa o overlay `select` antes de pintar; a selecção do store fica.
 * - A sincronização selecção para `select` é assíncrona e pode chegar
 *   depois de pintar: pinta-se outra vez no frame seguinte.
 * - Um contador de geração: um pedido que já não é o último desiste em
 *   vez de escrever por cima (desligar a meio de um aplicar não deixa o
 *   fantasma preso).
 * - O fantasma volta ao estado em que estava antes do realce.
 *
 * Diferença em relação aos conflitos: os estilos daqui levam prioridade
 * acima do fantasma. O fantasma do viewer põe tudo o que não está
 * seleccionado, e aqui os elementos pintados não têm de estar todos
 * seleccionados (clicar numa linha selecciona só essa); com a prioridade,
 * a cor de cada elemento ganha ao cinzento do fantasma.
 */

/** Acima do fantasma e dos outros overlays (prioridade 0), abaixo do
 *  `select`, que o Highlighter põe sempre no topo. */
export const PRIORIDADE_DO_REALCE = 1000

const PREFIXO = 'bcf-elemento-'

export interface ItemPintado {
  cor: string
  alvos: { modelId: string; localId: number }[]
}

interface HighlighterLike {
  styles: { has: (k: string) => boolean; set: (k: string, v: unknown) => void }
  clear: (name?: string) => Promise<void>
  highlightByID: (
    name: string,
    map: Record<string, Set<number>>,
    removePrevious?: boolean,
    zoomToSelection?: boolean,
  ) => Promise<void>
}

function hl(): HighlighterLike | null {
  return viewerApi.getHighlighter() as unknown as HighlighterLike | null
}

export function estiloDaCor(cor: string): string {
  return PREFIXO + cor.replace('#', '').toLowerCase()
}

function garantirEstilo(h: HighlighterLike, cor: string): string {
  const nome = estiloDaCor(cor)
  if (!h.styles.has(nome)) {
    h.styles.set(nome, {
      color: new THREE.Color(cor),
      opacity: 1,
      transparent: false,
      renderedFaces: RenderedFaces.TWO,
      priority: PRIORIDADE_DO_REALCE,
    })
  }
  return nome
}

/** O estado antes do realce, e os estilos que ele pôs. */
let estado: { fantasmaAntes: boolean; estilos: Set<string> } | null = null
let geracao = 0

export function realceActivo(): boolean {
  return estado !== null
}

function proximoFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'undefined') {
      setTimeout(resolve, 16)
      return
    }
    requestAnimationFrame(() => resolve())
  })
}

function porCor(itens: readonly ItemPintado[]): Map<string, Record<string, Set<number>>> {
  const out = new Map<string, Record<string, Set<number>>>()
  for (const it of itens) {
    if (it.alvos.length === 0) continue
    const mapa = out.get(it.cor) ?? {}
    for (const a of it.alvos) (mapa[a.modelId] ??= new Set<number>()).add(a.localId)
    out.set(it.cor, mapa)
  }
  return out
}

async function pintar(h: HighlighterLike, itens: readonly ItemPintado[]): Promise<void> {
  if (!estado) return
  try {
    await h.clear('select')
    const grupos = porCor(itens)
    // As cores que deixaram de ter elementos saem.
    for (const nome of estado.estilos) {
      if (![...grupos.keys()].some((c) => estiloDaCor(c) === nome)) await h.clear(nome)
    }
    for (const [cor, mapa] of grupos) {
      const nome = garantirEstilo(h, cor)
      estado.estilos.add(nome)
      await h.highlightByID(nome, mapa, true, false)
    }
  } catch (err) {
    console.debug('[bcf] realce dos elementos ignorado', err)
  }
}

export interface OpcoesDoRealce {
  /** Forçar as cores (um gesto explícito, como "Cores distintas"); sem
   *  isto vale a preferência Cores. */
  cores?: boolean
  /** Pode pôr a caixa de corte se a preferência Caixa estiver ligada.
   *  Fica de fora quando a vista do ficheiro BCF já traz os cortes. */
  caixa?: boolean
}

/** O último pedido, para voltar a aplicar quando as preferências mudam. */
let ultimo: { itens: ItemPintado[]; opcoes: OpcoesDoRealce } | null = null
/** A caixa de corte foi posta por este realce. */
let caixaPosta = false
let largarOuvinte: (() => void) | null = null

/** Pinta cada elemento da sua cor e põe o resto em fantasma, conforme as
 *  preferências do realce (Cores, Fantasma, Caixa: viewer/realce). Chamar
 *  outra vez com outros itens repinta (por exemplo depois de uma
 *  selecção feita no painel). */
export async function aplicarRealce(
  itens: readonly ItemPintado[],
  opcoes: OpcoesDoRealce = {},
): Promise<void> {
  ultimo = { itens: [...itens], opcoes }
  if (!largarOuvinte) {
    // Ligar ou desligar Cores, Fantasma ou Caixa volta a aplicar já.
    largarOuvinte = useAppStore.subscribe((s, antes) => {
      if (s.realce !== antes.realce && estado && ultimo) void aplicarComPreferencias()
    })
  }
  await aplicarComPreferencias()
}

async function aplicarComPreferencias(): Promise<void> {
  const h = hl()
  if (!h || !ultimo) return
  const { itens, opcoes } = ultimo
  const g = ++geracao
  if (!estado) {
    estado = { fantasmaAntes: useAppStore.getState().ghostMode, estilos: new Set() }
  }
  const prefs = useAppStore.getState().realce
  await viewerApi.setGhostMode(prefs.fantasma ? true : estado.fantasmaAntes)
  if (g !== geracao) return
  if (prefs.caixa && opcoes.caixa) {
    const ok = await caixaDeCorteA(itens.flatMap((i) => i.alvos))
    caixaPosta = caixaPosta || ok
  } else if (caixaPosta) {
    useAppStore.getState().setSectionEnabled(false)
    caixaPosta = false
  }
  if (g !== geracao) return
  if (!(opcoes.cores ?? prefs.cores)) {
    await tirarCores(h)
    await reporSeleccao(h)
    return
  }
  await pintar(h, itens)
  await proximoFrame()
  if (g !== geracao) return
  await pintar(h, itens)
}

async function tirarCores(h: HighlighterLike): Promise<void> {
  if (!estado) return
  for (const nome of estado.estilos) {
    try {
      await h.clear(nome)
    } catch (err) {
      console.debug('[bcf] tirar cores ignorado', err)
    }
  }
  estado.estilos.clear()
}

/** Tira o realce: as cores saem, o fantasma volta ao que estava e a
 *  selecção volta a âmbar. Idempotente. */
export async function limparRealce(): Promise<void> {
  const anterior = estado
  estado = null
  ultimo = null
  largarOuvinte?.()
  largarOuvinte = null
  geracao += 1
  if (caixaPosta) {
    useAppStore.getState().setSectionEnabled(false)
    caixaPosta = false
  }
  if (!anterior) return
  const h = hl()
  if (h) {
    for (const nome of anterior.estilos) {
      try {
        await h.clear(nome)
      } catch (err) {
        console.debug('[bcf] limpar realce ignorado', err)
      }
    }
  }
  await viewerApi.setGhostMode(anterior.fantasmaAntes)
  await reporSeleccao(h)
}

/** Repõe o âmbar da selecção a partir do store, a fonte de verdade. */
async function reporSeleccao(h: HighlighterLike | null): Promise<void> {
  if (!h) return
  const alvos = useAppStore.getState().selectedTargets
  if (alvos.length === 0) return
  const mapa: Record<string, Set<number>> = {}
  for (const t of alvos) (mapa[t.modelId] ??= new Set<number>()).add(t.localId)
  try {
    await h.highlightByID('select', mapa, true, false)
  } catch (err) {
    console.debug('[bcf] repor selecção ignorado', err)
  }
}
